import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import useAuth from "../../hooks/useAuth";
import Icons from "../icons";
import loginbg from "../../assets/loginbg.png";
import "../../pages/styles/PasswordReset.css";

const PasswordResetForm = () => {
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({ mode: "onChange" });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const password = watch("password");

  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  const {
    newPassword,
    isResetLoading,
    resetError,
    resetPasswordSuccess,
    clearErrors,
    clearSuccess,
  } = useAuth();

  // Guard: must have email from router state
  useEffect(() => {
    if (!email) navigate("/forgot-password");
  }, [email, navigate]);

  // Navigate to login on success
useEffect(() => {
  if (resetPasswordSuccess) {
    clearSuccess();
    setSuccessMsg("Password changed successfully! Redirecting to login…");
    setTimeout(() => navigate("/", { replace: true }), 1500);
  }
}, [resetPasswordSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => clearErrors();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = (data) => {
    newPassword(email, data.password);
  };

  return (
    <div className="reset-wrapper">
      <div className="reset-left">
        <img src={loginbg} alt="illustration" />
        <p>
          Unleash Your Team's Potential With QwickHub <br />
          Built For Modern Product Teams
        </p>
      </div>

      <div className="reset-right">
        <div className="absolute top-4 right-6 flex items-center gap-0 cursor-pointer">
          <Icons.Logo />
          <span className="text-xl font-semibold">QwickHub</span>
        </div>

        <div className="reset-card">
          <h1 className="reset-title title text-3xl font-semibold p-5">
            Reset Your Password
          </h1>

          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Backend error from Redux */}
            {resetError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{resetError}</p>
              </div>
            )}
            {successMsg && (
  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
    <p className="text-sm text-green-600">{successMsg}</p>
  </div>
)}

            {/* Password */}
            <div>
              <label>Password</label>
              <div className="reset-password-field">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  disabled={isResetLoading}
                  {...register("password", {
                    required: "Password is required",
                    minLength: {
                      value: 8,
                      message: "Must be at least 8 characters",
                    },
                  })}
                />
                <span onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
              {errors.password && (
                <span className="reset-error">{errors.password.message}</span>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label>Confirm Password</label>
              <div className="reset-password-field">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  disabled={isResetLoading}
                  {...register("confirmPassword", {
                    required: "Please confirm your password",
                    validate: (value) =>
                      value === password || "Passwords do not match",
                  })}
                />
                <span
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                </span>
              </div>
              {errors.confirmPassword && (
                <span className="reset-error">
                  {errors.confirmPassword.message}
                </span>
              )}
            </div>
            <div className="reset-links-row">
              <span className="reset-contact-text">
                Any Queries? Contact{" "}
                <a href="https://qwickbit.com/">Qwickbit</a>
              </span>
            </div>

            <button
              type="submit"
              disabled={isResetLoading || !isValid}
              className="reset-btn"
            >
              {isResetLoading ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          <p className="reset-back">
            <a href="/">Back to Login</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetForm;
